/**
 * @file: This file holds the controller of the Curator sync status.
 *
 * The server syncs the posts from Curator on a timer. When the sync fails, no
 * new posts come in, and the Approver page shows an empty list. The operator
 * needs to know why, so this box shows the failure, the time it started and
 * the time of the last success. The box shows nothing while the sync works.
 *
 * The state can last for days, so it is a box on the page and not a toast.
 *
 * The server sends a change only one time. A client that is disconnected at
 * that time does not get it. The controller therefore reads the status again
 * after each reconnect.
 */

import moment from 'moment'
import { CURATOR_SYNC_STATUS_EVENT, CURATOR_FAILURE_KINDS } from '../../../shared/curator-sync'
import './curator-sync-status.component.less'

/** The text for each kind of failure. */
const HEADLINES = Object.freeze({
  [CURATOR_FAILURE_KINDS.PLAN]: 'Curator refuses the API. The Curator plan has no API access.',
  [CURATOR_FAILURE_KINDS.KEY]: 'Curator refuses the API key.',
  [CURATOR_FAILURE_KINDS.OTHER]: 'The Curator sync fails.'
})

const formatTime = iso => moment(iso).format('DD/MM HH:mm')

/**
 * The failure as the box shows it, or null when there is no failure. A server
 * from an older build sends no failingSince, so the start time can be null.
 */
function describeForOperator (status) {
  if (!status || !status.failure) {
    return null
  }

  return Object.freeze({
    headline: HEADLINES[status.failure.kind] || HEADLINES[CURATOR_FAILURE_KINDS.OTHER],
    since: status.failingSince ? formatTime(status.failingSince) : null,
    lastSuccess: status.succeededAt
      ? `Last sync that worked: ${formatTime(status.succeededAt)}.`
      : 'No sync worked since the server started.',
    message: status.failure.message
  })
}

class CuratorSyncStatusController {
  constructor ($log, ApiService) {
    'ngInject'
    this._$log = $log
    this._ApiService = ApiService
    this.failure = null
    // The count of change events. A read that started before an event has an older status.
    this._eventCount = 0
    this._onChanged = (error, status) => {
      if (error) {
        return
      }
      this._eventCount += 1
      this.failure = describeForOperator(status)
    }
  }

  $onInit () {
    this._ApiService.on(CURATOR_SYNC_STATUS_EVENT, this._onChanged)
    this._offReconnected = this._ApiService.onReconnected(() => this.readStatus())
    this.readStatus()
  }

  $onDestroy () {
    this._ApiService.off(CURATOR_SYNC_STATUS_EVENT, this._onChanged)
    this._offReconnected()
  }

  /** Read the status from the server. Ignore the answer when a change event came first. */
  readStatus () {
    const eventCount = this._eventCount
    return this._ApiService.call('curator/getSyncStatus')
      .then(status => {
        if (eventCount === this._eventCount) {
          this.failure = describeForOperator(status)
        }
      })
      .catch(error => {
        this._$log.error('[CuratorSyncStatus] Could not read the Curator sync status', error)
      })
  }
}

export { CuratorSyncStatusController, HEADLINES }

export default {
  controller: CuratorSyncStatusController,
  template: require('./curator-sync-status.tpl.html').default
}
